import './smart-app-banner.scss';

import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as AppstoreIcon } from '/src/images/icons/app-store-icon.svg';
import { ReactComponent as Close } from '/src/images/icons/close-normal.svg';
import { ReactComponent as PlaystoreIcon } from '/src/images/icons/play-store-icon.svg';
import { ReactComponent as Logo } from '/src/images/icons/smart-banner-logo.svg';

import {
  getAppstoreLanguage,
  getLocalStorageItem,
  getUserAgent,
  isClient,
  setLocalStorageItem,
} from '../../helpers/utils';
import { useGetLibraryDictionary } from '../../hooks/use-get-library-dictionary';
import { IStoreImage } from '../../queries/fragments/storeImage';

interface SmartBannerProps {
  appStoreLanguage?: string;
  title?: string;
  url?: { [key: string]: string };
  appMeta?: {
    android: string;
    ios: string;
  };
  onClose?: () => void;
  onInstall?: () => void;
  appStoreImage?: IStoreImage;
  playStoreImage?: IStoreImage;
  node_locale: string;
}

interface ISettings {
  appMeta: string;
  getStoreLink: () => string;
}

type DeviceType = 'ios' | 'android' | '';

const SmartAppBanner: React.FC<SmartBannerProps> = ({
  appStoreLanguage = getAppstoreLanguage(),

  title = '',
  url = {
    ios: '',
    android: '',
  },
  appMeta = {
    ios: 'apple-itunes-app',
    android: 'google-play-app',
  },
  onClose,
  onInstall,
  appStoreImage,
  playStoreImage,
  node_locale,
}) => {
  const [type, setType] = useState<DeviceType>('');
  const [appId, setAppId] = useState<string>('');
  const [settings, setSettings] = useState<ISettings | null>(null);

  const dictionary = useGetLibraryDictionary(node_locale);

  useEffect(() => {
    if (!isClient) return;

    const ua = getUserAgent();
    if (ua) {
      const osName = ua.os?.name;
      if (osName === 'iOS' && ua.browser.name !== 'Mobile Safari') {
        setType('ios');
      } else if (osName === 'Android') {
        setType('android');
      }
    }
  }, []);

  const settingsMap = useMemo(
    () => ({
      ios: {
        appMeta: appMeta.ios,
        getStoreLink: () =>
          `https://apps.apple.com/${appStoreLanguage}/app/livat/id`,
      },
      android: {
        appMeta: appMeta.android,
        getStoreLink: () => 'http://play.google.com/store/apps/details?id=',
      },
    }),
    [appMeta, appStoreLanguage],
  );

  useEffect(() => {
    if (type) {
      setSettings(settingsMap[type]);
    }
  }, [type]);

  useEffect(() => {
    if (settings && isClient) {
      const meta = window.document.querySelector(
        `meta[name="${settings.appMeta}"]`,
      );
      if (!meta) return;

      let parsedAppId = '';
      const content = /app-id=([^\s,]+)/.exec(
        meta.getAttribute('content') || '',
      );
      parsedAppId = content && content[1] ? content[1] : '';

      setAppId(parsedAppId);
    }
  }, [settings]);

  const hide = useCallback(() => {
    if (isClient) {
      window.document
        .querySelector('html')
        ?.classList.remove('smartbanner-show');
    }
  }, []);

  const show = useCallback(() => {
    if (isClient) {
      window.document.querySelector('html')?.classList.add('smartbanner-show');
    }
  }, []);

  const handleClose = useCallback(() => {
    hide();
    setLocalStorageItem('smartbanner-closed', 'true');

    if (onClose) {
      onClose();
    }
  }, [hide, onClose]);

  const handleInstall = useCallback(() => {
    hide();
    setLocalStorageItem('smartbanner-installed', 'true');

    if (onInstall) {
      onInstall();
    }
  }, [hide, onInstall]);

  const retrieveInfo = () => {
    const link = url[type] || (settings ? settings.getStoreLink() + appId : '');

    return { link };
  };

  if (!isClient) return null;

  if (
    !type ||
    getLocalStorageItem('smartbanner-closed') ||
    getLocalStorageItem('smartbanner-installed')
  ) {
    return null;
  }

  if (!appId) {
    return null;
  }

  show();

  const { link } = retrieveInfo();

  const getStoreImage = (
    type: DeviceType,
    appStoreImage: IStoreImage | undefined,
    playStoreImage: IStoreImage | undefined,
  ) => {
    if (type === 'ios') {
      return appStoreImage ? (
        <GatsbyImage
          alt={appStoreImage?.description}
          image={appStoreImage.gatsbyImageData}
        />
      ) : (
        <AppstoreIcon />
      );
    } else if (type === 'android') {
      return playStoreImage ? (
        <GatsbyImage
          alt={playStoreImage?.description}
          image={playStoreImage.gatsbyImageData}
        />
      ) : (
        <PlaystoreIcon />
      );
    }
    return null;
  };

  return (
    <div className="smartbanner">
      <div className="flex h-15 items-center">
        <button type="button" aria-label="close" onClick={handleClose}>
          <Close className="h-2 w-2 cursor-pointer from-neutral-grey-400" />
        </button>
        <Logo className="h-12 w-12 pl-2" />
        <div className="flex-col pl-3">
          <div className="font-title text-headingxs">{title}</div>
          <div className="text-bodys">{dictionary?.isAvailableOn}</div>
        </div>
        <div className="ml-auto">
          <a href={link} onClick={handleInstall}>
            {getStoreImage(type, appStoreImage, playStoreImage)}
          </a>
        </div>
      </div>
    </div>
  );
};

export default SmartAppBanner;
